import React, { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Button, Form, Table, Alert, Spinner, Badge } from "react-bootstrap";
import "../Accounts/acc-list.css";
import { jwtDecode } from "jwt-decode";

// Pull these from your central API file where you added them previously
import {
  fetchSessionsByPractices,
  bulkLinkSessionsAndAppointments,
} from "../../api";

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

// Intake-sourced sessions (materialized from an online intake form pending
// review) have no appointment — fall back to the name/date stamped on the
// session doc itself.
function sessionFirstName(s) {
  return s?.appointment?.patientFirstName || s?.sessionsIntakeName?.firstName || "";
}
function sessionLastName(s) {
  return s?.appointment?.patientLastName || s?.sessionsIntakeName?.lastName || "";
}
function sessionStart(s) {
  return s?.appointment?.start || s?.sessionsAppointmentStart || s?.createdAt || null;
}
function isIntakeSession(s) {
  return typeof s?.sessionsSource === "string" && s.sessionsSource.startsWith("intake");
}

export function LinkNotes({ patientId, patientFirstName, patientLastName, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]); // raw list from API
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set()); // Set of session _id strings
  const [linking, setLinking] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // ---- Fetch unlinked sessions for user's practices ----
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      setSuccessMsg("");
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("Not authenticated");

        const decoded = jwtDecode(token);
        const practices = Array.isArray(decoded?.practice) ? decoded.practice : [];
        if (!practices.length) {
          setSessions([]);
          setError("No practices found in your account.");
          return;
        }

        // Pull sessions where sessionsPatientId === "1" (unlinked)
        const data = await fetchSessionsByPractices(practices, "1");
        if (!cancelled) {
          // Extract sessions array from response
          console.log("API Response:", data);
          const sessionsList = data?.sessions || data || [];
          console.log("Sessions List:", sessionsList);
          setSessions(Array.isArray(sessionsList) ? sessionsList : []);
          setSelected(new Set());
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load sessions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Filter/search and split into suggested/all ----
  const { suggested, others } = useMemo(() => {
    console.log("Sessions in useMemo:", sessions);
    console.log("Patient info:", { patientFirstName, patientLastName });
    
    const q = search.trim().toLowerCase();
    let list = sessions;

    // Apply search filter if query exists
    if (q) {
      list = sessions.filter((s) => {
        const textParts = [
          sessionFirstName(s),
          sessionLastName(s),
          s?.sessionsServiceName,
          s?.notepadContent,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return textParts.includes(q);
      });
    }

    // Split into suggested and others
    const suggestedList = [];
    const othersList = [];

    const patientFirst = (patientFirstName || "").toLowerCase().trim();
    const patientLast = (patientLastName || "").toLowerCase().trim();

    list.forEach((s) => {
      const apptFirst = sessionFirstName(s).toLowerCase().trim();
      const apptLast = sessionLastName(s).toLowerCase().trim();

      console.log("Comparing:", {
        patient: { patientFirst, patientLast },
        appointment: { apptFirst, apptLast }
      });

      // Check if names match (contains comparison)
      const firstMatch = patientFirst && apptFirst.includes(patientFirst);
      const lastMatch = patientLast && apptLast.includes(patientLast);

      if ((firstMatch || lastMatch) && (patientFirst || patientLast)) {
        suggestedList.push(s);
      } else {
        othersList.push(s);
      }
    });

    console.log("Suggested:", suggestedList.length, "Others:", othersList.length);
    return { suggested: suggestedList, others: othersList };
  }, [search, sessions, patientFirstName, patientLastName]);

  const filtered = [...suggested, ...others];
  console.log("Filtered total:", filtered.length);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((s) => selected.has(String(s._id)));

  // ---- Selection handlers ----
  const toggleOne = (id) => {
    const k = String(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // unselect all visible
        filtered.forEach((s) => next.delete(String(s._id)));
      } else {
        // select all visible
        filtered.forEach((s) => next.add(String(s._id)));
      }
      return next;
    });
  };

  // ---- Link action ----
  const handleLink = async () => {
    setError("");
    setSuccessMsg("");
    if (!patientId) {
      setError("Missing patientId for linking.");
      return;
    }
    const sessionIds = Array.from(selected);
    if (sessionIds.length === 0) return;

    // Gather appointmentIds from the selected sessions (if present)
    const appointmentIds = sessions
      .filter((s) => sessionIds.includes(String(s._id)) && s?.sessionsAppointmentId)
      .map((s) => String(s.sessionsAppointmentId));

    const forceNumber = /^\d+$/.test(String(patientId));

    setLinking(true);
    try {
      const res = await bulkLinkSessionsAndAppointments({
        sessionIds,
        appointmentIds, // optional; server will also try to derive where possible
        patientId,
        forceNumber,
      });

      const modifiedSessions = res?.modifiedSessions ?? 0;
      const modifiedAppointments = res?.modifiedAppointments ?? 0;

      setSuccessMsg(
        `Successfully linked ${modifiedSessions} session(s)` +
          (modifiedAppointments > 0
            ? ` and updated ${modifiedAppointments} appointment(s).`
            : ".")
      );

      // Refresh list (remove linked from current view)
      setSessions((prev) =>
        prev.filter((s) => !sessionIds.includes(String(s._id)))
      );
      setSelected(new Set());
      
      // Call the onSuccess callback to switch back to historical
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setError(e?.message || "Failed to link selected sessions.");
    } finally {
      setLinking(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <Row className="align-items-center mb-3">
          <Col>
            <div className="text-muted small">
              Select one or more unlinked sessions and link them to this patient.
            </div>
          </Col>
          <Col md="auto" className="mt-2 mt-md-0">
            <Form.Control
              placeholder="Search by patient name or service"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert variant="success" className="mb-3">
            {successMsg}
          </Alert>
        )}

        {loading ? (
          <div className="d-flex justify-content-center my-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : filtered.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No unlinked sessions found for your practices.
          </Alert>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Check
                type="checkbox"
                id="select-all-visible"
                label="Select all (visible)"
                checked={allVisibleSelected}
                onChange={toggleAllVisible}
              />
              <div className="text-muted small">
                {selected.size} selected
              </div>
            </div>

            <div className="table-responsive">
              <Table hover size="sm" className="align-middle mb-3">
                <thead className="text-center">
                  <tr>
                    <th className="text-center align-middle text-primary" style={{ width: 36 }} />
                    <th className="text-center align-middle text-primary">Date &amp; Time</th>
                    <th className="text-center align-middle text-primary">Patient Name</th>
                  </tr>
                </thead>
                <tbody>
                  {suggested.length > 0 && (
                    <>
                      <tr>
                        <td
                          colSpan="3"
                          className="fw-bold text-primary"
                          style={{ background: "var(--acc-header-bg)" }}
                        >
                          Suggested
                        </td>
                      </tr>
                      {suggested.map((s) => {
                        const id = String(s._id);
                        const checked = selected.has(id);
                        const fullName = `${sessionFirstName(s)} ${sessionLastName(s)}`.trim() || "—";
                        return (
                          <tr key={id}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleOne(id)}
                                aria-label={`select session ${id}`}
                              />
                            </td>
                            <td>{formatDateTime(sessionStart(s))}</td>
                            <td>
                              {fullName}
                              {isIntakeSession(s) && (
                                <Badge bg="secondary" className="ms-2">Intake</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                  
                  {others.length > 0 && (
                    <>
                      <tr>
                        <td
                          colSpan="3"
                          className="fw-bold text-primary"
                          style={{ background: "var(--acc-header-bg)" }}
                        >
                          All Sessions
                        </td>
                      </tr>
                      {others.map((s) => {
                        const id = String(s._id);
                        const checked = selected.has(id);
                        const fullName = `${sessionFirstName(s)} ${sessionLastName(s)}`.trim() || "—";
                        return (
                          <tr key={id}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleOne(id)}
                                aria-label={`select session ${id}`}
                              />
                            </td>
                            <td>{formatDateTime(sessionStart(s))}</td>
                            <td>
                              {fullName}
                              {isIntakeSession(s) && (
                                <Badge bg="secondary" className="ms-2">Intake</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                onClick={handleLink}
                disabled={linking || selected.size === 0}
              >
                {linking ? "Linking…" : "Link Selected"}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}